import { useRef } from 'react';
import {
    Badge,
    Box,
    Button,
    Checkbox,
    HStack,
    IconButton,
    Text,
    Tooltip,
    VStack,
} from '@chakra-ui/react';
import { RefreshCw, Trash2, Upload, X } from 'lucide-react';
import { ManageDialogFrame, ManageDialogCloseFooter } from './ManageDialogFrame';
import { useLispScriptsStore } from '../../state/useLispScriptsStore';
import { useAppStore } from '../../state/store';
import { loadLispSource, unloadLispSource } from '../../core/lisp';
import { notify } from '../../utils/notify';

/**
 * APPLOAD dialog — Load/Unload/Reload AutoLISP (.lsp) files, mark them for
 * the startup suite and inspect per-script errors. Scripts persist in the
 * script library store so the startup suite can re-load them at boot.
 */
export function AppLoadDialog() {
    const closeDialog = useAppStore((s) => s.closeManageDialog);
    const scripts = useLispScriptsStore((s) => s.scripts);
    const store = {
        addScript: useLispScriptsStore.getState().addScript,
        removeScript: useLispScriptsStore.getState().removeScript,
        setStatus: useLispScriptsStore.getState().setStatus,
        toggleStartup: useLispScriptsStore.getState().toggleStartup,
    };
    const fileInputRef = useRef<HTMLInputElement>(null);

    const loadOne = async (id: string, content: string) => {
        const result = await loadLispSource(content, id);
        store.setStatus(id, result.ok ? 'loaded' : 'error', result.message, result.commands);
        if (result.ok) {
            notify(
                `${result.commands.length > 0 ? `Loaded — new commands: ${result.commands.join(', ')}` : 'Loaded.'}`,
                'success'
            );
        } else {
            notify(`LISP load failed: ${result.message}`, 'error');
        }
    };

    const handleFiles = async (files: FileList) => {
        for (const file of Array.from(files)) {
            const content = await file.text();
            const id = store.addScript(file.name, content);
            if (!id) {
                notify(`"${file.name}" is too large to store in the script library.`, 'error');
                continue;
            }
            await loadOne(id, content);
        }
    };

    const handleUnload = (id: string, name: string) => {
        const removed = unloadLispSource(id);
        store.setStatus(id, 'unloaded', `Unloaded (${removed} command(s) removed).`, []);
    };

    const handleReload = async (id: string) => {
        const script = useLispScriptsStore.getState().scripts.find((s) => s.id === id);
        if (!script) return;
        unloadLispSource(id);
        store.setStatus(id, 'unloaded', 'Reloading…', []);
        await loadOne(id, script.content);
    };

    return (
        <ManageDialogFrame
            title="Load Application (APPLOAD)"
            isOpen
            onClose={closeDialog}
            size="lg"
            footer={<ManageDialogCloseFooter onClose={closeDialog} />}
        >
            <VStack align="stretch" spacing={3} minH="40vh">
                <HStack spacing={2}>
                    <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<Upload size={14} />}
                        flexShrink={0}
                        whiteSpace="nowrap"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Load LISP file(s)…
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".lsp,.txt,text/plain"
                        multiple
                        className="hidden-file-input"
                        onChange={(e) => {
                            if (e.target.files?.length) void handleFiles(e.target.files);
                            e.target.value = '';
                        }}
                    />
                    <Text fontSize="xs" color="text.secondary" minW={0}>
                        .lsp sources are sandboxed: no host file/network access, Esc cancels a running script.
                    </Text>
                </HStack>

                <Box flex={1} overflowY="auto" borderWidth="1px" borderColor="border.base" borderRadius="md" minH="180px">
                    {scripts.length === 0 ? (
                        <Text p={4} fontSize="sm" color="text.secondary">
                            No LISP files loaded yet. Load a .lsp file above — (defun c:MYCMD …)
                            definitions become callable commands.
                        </Text>
                    ) : (
                        <VStack align="stretch" spacing={0}>
                            {scripts.map((script) => (
                                <Box key={script.id} p={2} borderBottomWidth="1px" borderColor="border.base">
                                    <HStack spacing={2} justify="space-between" flexWrap="nowrap">
                                        <HStack spacing={2} minW={0}>
                                            <Badge
                                                colorScheme={
                                                    script.status === 'loaded'
                                                        ? 'green'
                                                        : script.status === 'error'
                                                          ? 'red'
                                                          : 'gray'
                                                }
                                                flexShrink={0}
                                            >
                                                {script.status}
                                            </Badge>
                                            <Text fontSize="sm" noOfLines={1} minW={0}>
                                                {script.name}
                                            </Text>
                                            {script.commands.length > 0 && (
                                                <Text fontSize="xs" color="text.secondary" whiteSpace="nowrap" flexShrink={0}>
                                                    {script.commands.length} command(s)
                                                </Text>
                                            )}
                                        </HStack>
                                        <HStack spacing={1} flexShrink={0}>
                                            <Checkbox
                                                size="sm"
                                                isChecked={script.loadAtStartup}
                                                onChange={() => store.toggleStartup(script.id)}
                                            >
                                                Startup
                                            </Checkbox>
                                            <Tooltip label="Reload">
                                                <IconButton
                                                    aria-label="Reload"
                                                    icon={<RefreshCw size={13} />}
                                                    size="xs"
                                                    variant="outline"
                                                    onClick={() => void handleReload(script.id)}
                                                />
                                            </Tooltip>
                                            <Tooltip label="Unload">
                                                <IconButton
                                                    aria-label="Unload"
                                                    icon={<X size={13} />}
                                                    size="xs"
                                                    variant="outline"
                                                    isDisabled={script.status !== 'loaded'}
                                                    onClick={() => handleUnload(script.id, script.name)}
                                                />
                                            </Tooltip>
                                            <Tooltip label="Remove from library">
                                                <IconButton
                                                    aria-label="Remove"
                                                    icon={<Trash2 size={13} />}
                                                    size="xs"
                                                    variant="outline"
                                                    colorScheme="red"
                                                    onClick={() => {
                                                        unloadLispSource(script.id);
                                                        store.removeScript(script.id);
                                                    }}
                                                />
                                            </Tooltip>
                                        </HStack>
                                    </HStack>
                                    {script.message && (
                                        <Text fontSize="xs" color={script.status === 'error' ? 'red.400' : 'text.secondary'} mt={1} fontFamily="mono">
                                            {script.message}
                                        </Text>
                                    )}
                                </Box>
                            ))}
                        </VStack>
                    )}
                </Box>
            </VStack>
        </ManageDialogFrame>
    );
}
